/**
 * SectionPanel - 右ペイン（セクション一覧）
 * PoC frontend/src/section-classifier/components/SectionPanel.tsx から移植
 */

import type { SectionDefinition, SectionAssignment, TranscriptUtterance } from '../types';

interface SectionPanelProps {
  sections: SectionDefinition[];
  assignments: SectionAssignment[];
  utterances: TranscriptUtterance[];
  activeSectionId: string | null;
  clickPhase: 'idle' | 'start' | 'end';
  onSectionSelect: (sectionId: string) => void;
  onSectionRemove: (assignment: SectionAssignment) => void;
  onRangeAdd?: (sectionId: string) => void;
  isSubmitting?: boolean;
}

export function SectionPanel({
  sections,
  assignments,
  utterances,
  activeSectionId,
  clickPhase,
  onSectionSelect,
  onSectionRemove,
  onRangeAdd,
  isSubmitting = false,
}: SectionPanelProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700">セクション一覧</h2>
        <p className="text-xs text-gray-500 mt-1">
          セクションを選択 → 始点クリック → 終点クリック
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
        {sections.map((section) => {
          const matchingAssignments = assignments.filter(a => a.sectionId === section.id);
          const assignment = matchingAssignments[0];
          const rangeCount = matchingAssignments.length;
          const isActive = activeSectionId === section.id;
          const isAssigned = !!assignment;
          const isMulti = section.allowMultipleRanges === true;
          const startTime = isAssigned
            ? utterances[assignment.startUtteranceIndex]?.timestamp || '--:--'
            : null;
          const endTime = isAssigned
            ? utterances[assignment.endUtteranceIndex]?.timestamp || '--:--'
            : null;

          return (
            <div key={section.id} className="relative">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => onSectionSelect(section.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  isActive
                    ? `${section.bgColor} border-current ${section.textColor} ring-2 ring-offset-1 ring-current`
                    : isAssigned
                      ? `${section.bgColor} border-transparent ${section.textColor}`
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isAssigned ? (
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                        isActive ? 'border-current bg-current' : 'border-gray-300'
                      }`} />
                    )}
                    <span className="text-sm font-medium">
                      {section.name}
                      {isMulti && rangeCount > 0 && (
                        <span className="ml-1 text-xs font-normal opacity-70">× {rangeCount}</span>
                      )}
                    </span>
                  </div>

                  <span className="text-xs font-mono opacity-70">
                    {isAssigned ? `${formatShort(startTime!)} - ${formatShort(endTime!)}` : '--:--'}
                  </span>
                </div>

                {isActive && clickPhase === 'start' && (
                  <p className="text-xs mt-1.5 ml-6 opacity-80">
                    左の発話をクリックして開始位置を設定
                    {isMulti && '（複数範囲を追加可能）'}
                  </p>
                )}
                {isActive && clickPhase === 'end' && (
                  <p className="text-xs mt-1.5 ml-6 opacity-80">左の発話をクリックして終了位置を設定</p>
                )}
              </button>

              {isAssigned && !isActive && !isSubmitting && !isMulti && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSectionRemove(assignment);
                  }}
                  className="absolute top-1/2 -translate-y-1/2 right-1 p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="設定を解除"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}

              {isMulti && rangeCount > 0 && (
                <div className="mt-1 ml-6 space-y-1">
                  {matchingAssignments.map((range, index) => {
                    const rangeStart = utterances[range.startUtteranceIndex]?.timestamp || '--:--';
                    const rangeEnd = utterances[range.endUtteranceIndex]?.timestamp || '--:--';
                    return (
                      <div
                        key={`${section.id}-${range.startUtteranceIndex}-${range.endUtteranceIndex}`}
                        className="flex items-center justify-between gap-2 rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600"
                      >
                        <span className="min-w-0 truncate">
                          {section.name} {index + 1}
                        </span>
                        <span className="shrink-0 font-mono">
                          {formatShort(rangeStart)} - {formatShort(rangeEnd)}
                        </span>
                        {!isSubmitting && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSectionRemove(range);
                            }}
                            className="shrink-0 rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                            title="このサイクルを解除"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {isMulti && onRangeAdd && (
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => onRangeAdd(section.id)}
                  className="mt-1 ml-6 inline-flex items-center gap-1 rounded border border-cyan-400 bg-white px-2 py-0.5 text-xs font-medium text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
                  title="次の問題サイクルを追加"
                >
                  ＋ サイクル追加
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatShort(timestamp: string): string {
  const parts = timestamp.split(':');
  if (parts.length === 3) {
    const hours = Number(parts[0]);
    if (!Number.isNaN(hours) && hours > 0) return `${parts[0]}:${parts[1]}:${parts[2]}`;
    return `${parts[1]}:${parts[2]}`;
  }
  return timestamp;
}
